import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';

const BASE_URL = '/api/students/academic';

// Academic Terms
export const fetchAcademicTerms = createAsyncThunk(
  'academics/fetchAcademicTerms',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/academic-terms/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createAcademicTerm = createAsyncThunk(
  'academics/createAcademicTerm',
  async (termData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/academic-terms/`, termData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateAcademicTerm = createAsyncThunk(
  'academics/updateAcademicTerm',
  async ({ id, ...termData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/academic-terms/${id}/`, termData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteAcademicTerm = createAsyncThunk(
  'academics/deleteAcademicTerm',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/academic-terms/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Grade Scales
export const fetchGradeScales = createAsyncThunk(
  'academics/fetchGradeScales',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/grade-scales/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createGradeScale = createAsyncThunk(
  'academics/createGradeScale',
  async (scaleData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/grade-scales/`, scaleData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateGradeScale = createAsyncThunk(
  'academics/updateGradeScale',
  async ({ id, ...scaleData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/grade-scales/${id}/`, scaleData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteGradeScale = createAsyncThunk(
  'academics/deleteGradeScale',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/grade-scales/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Exams
export const fetchExams = createAsyncThunk(
  'academics/fetchExams',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/exams/`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchExam = createAsyncThunk(
  'academics/fetchExam',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/exams/${id}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createExam = createAsyncThunk(
  'academics/createExam',
  async (examData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/exams/`, examData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateExam = createAsyncThunk(
  'academics/updateExam',
  async ({ id, ...examData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/exams/${id}/`, examData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteExam = createAsyncThunk(
  'academics/deleteExam',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/exams/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchExamResults = createAsyncThunk(
  'academics/fetchExamResults',
  async (examId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/exams/${examId}/results/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchStudentExams = createAsyncThunk(
  'academics/fetchStudentExams',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/exams/by_student/?student_id=${studentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Exam Results
export const fetchExamResultsList = createAsyncThunk(
  'academics/fetchExamResultsList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/exam-results/`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createExamResult = createAsyncThunk(
  'academics/createExamResult',
  async (resultData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/exam-results/`, resultData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateExamResult = createAsyncThunk(
  'academics/updateExamResult',
  async ({ id, ...resultData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/exam-results/${id}/`, resultData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteExamResult = createAsyncThunk(
  'academics/deleteExamResult',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/exam-results/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Assignments
export const fetchAssignments = createAsyncThunk(
  'academics/fetchAssignments',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/assignments/`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAssignment = createAsyncThunk(
  'academics/fetchAssignment',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/assignments/${id}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createAssignment = createAsyncThunk(
  'academics/createAssignment',
  async (assignmentData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/assignments/`, assignmentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateAssignment = createAsyncThunk(
  'academics/updateAssignment',
  async ({ id, ...assignmentData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/assignments/${id}/`, assignmentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteAssignment = createAsyncThunk(
  'academics/deleteAssignment',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/assignments/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAssignmentSubmissions = createAsyncThunk(
  'academics/fetchAssignmentSubmissions',
  async (assignmentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/assignments/${assignmentId}/submissions/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchStudentAssignments = createAsyncThunk(
  'academics/fetchStudentAssignments',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/assignments/by_student/?student_id=${studentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Assignment Submissions
export const fetchAssignmentSubmissionsList = createAsyncThunk(
  'academics/fetchAssignmentSubmissionsList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/assignment-submissions/`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createAssignmentSubmission = createAsyncThunk(
  'academics/createAssignmentSubmission',
  async (submissionData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/assignment-submissions/`, submissionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateAssignmentSubmission = createAsyncThunk(
  'academics/updateAssignmentSubmission',
  async ({ id, ...submissionData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/assignment-submissions/${id}/`, submissionData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteAssignmentSubmission = createAsyncThunk(
  'academics/deleteAssignmentSubmission',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/assignment-submissions/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Grade Entries
export const fetchGradeEntries = createAsyncThunk(
  'academics/fetchGradeEntries',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/grade-entries/`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createGradeEntry = createAsyncThunk(
  'academics/createGradeEntry',
  async (gradeData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/grade-entries/`, gradeData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateGradeEntry = createAsyncThunk(
  'academics/updateGradeEntry',
  async ({ id, ...gradeData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/grade-entries/${id}/`, gradeData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteGradeEntry = createAsyncThunk(
  'academics/deleteGradeEntry',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/grade-entries/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchStudentGradeSummary = createAsyncThunk(
  'academics/fetchStudentGradeSummary',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/grade-entries/student_summary/`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Academic Plans
export const fetchAcademicPlans = createAsyncThunk(
  'academics/fetchAcademicPlans',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/academic-plans/`, { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAcademicPlan = createAsyncThunk(
  'academics/fetchAcademicPlan',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/academic-plans/${id}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createAcademicPlan = createAsyncThunk(
  'academics/createAcademicPlan',
  async (planData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/academic-plans/`, planData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateAcademicPlan = createAsyncThunk(
  'academics/updateAcademicPlan',
  async ({ id, ...planData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/academic-plans/${id}/`, planData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteAcademicPlan = createAsyncThunk(
  'academics/deleteAcademicPlan',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/academic-plans/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Reports
export const fetchStudentReportCard = createAsyncThunk(
  'academics/fetchStudentReportCard',
  async ({ studentId, academicTermId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/students/${studentId}/report-card/?academic_term_id=${academicTermId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchStudentTranscript = createAsyncThunk(
  'academics/fetchStudentTranscript',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/students/${studentId}/transcript/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Student Activities
export const fetchStudentActivities = createAsyncThunk(
  'academics/fetchStudentActivities',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/student-activities/?student=${studentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createStudentActivity = createAsyncThunk(
  'academics/createStudentActivity',
  async (activityData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/student-activities/`, activityData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateStudentActivity = createAsyncThunk(
  'academics/updateStudentActivity',
  async ({ id, ...activityData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/student-activities/${id}/`, activityData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteStudentActivity = createAsyncThunk(
  'academics/deleteStudentActivity',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/student-activities/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Student Achievements
export const fetchStudentAchievements = createAsyncThunk(
  'academics/fetchStudentAchievements',
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE_URL}/student-achievements/?student=${studentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createStudentAchievement = createAsyncThunk(
  'academics/createStudentAchievement',
  async (achievementData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE_URL}/student-achievements/`, achievementData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateStudentAchievement = createAsyncThunk(
  'academics/updateStudentAchievement',
  async ({ id, ...achievementData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`${BASE_URL}/student-achievements/${id}/`, achievementData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteStudentAchievement = createAsyncThunk(
  'academics/deleteStudentAchievement',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/student-achievements/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const academicsSlice = createSlice({
  name: 'academics',
  initialState: {
    // Academic Terms
    academicTerms: [],
    // Grade Scales
    gradeScales: [],
    // Exams
    exams: [],
    currentExam: null,
    examResults: [],
    // Assignments
    assignments: [],
    currentAssignment: null,
    assignmentSubmissions: [],
    // Grade Entries
    gradeEntries: [],
    gradeSummary: null,
    // Academic Plans
    academicPlans: [],
    currentPlan: null,
    // Student Activities
    studentActivities: [],
    // Student Achievements
    studentAchievements: [],
    // Reports
    reportCard: null,
    transcript: null,
    // UI State
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearReportCard: (state) => {
      state.reportCard = null;
    },
    clearTranscript: (state) => {
      state.transcript = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Academic Terms
      .addCase(fetchAcademicTerms.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAcademicTerms.fulfilled, (state, action) => {
        state.loading = false;
        state.academicTerms = action.payload;
      })
      .addCase(fetchAcademicTerms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createAcademicTerm.fulfilled, (state, action) => {
        state.academicTerms.push(action.payload);
      })
      .addCase(updateAcademicTerm.fulfilled, (state, action) => {
        const index = state.academicTerms.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) {
          state.academicTerms[index] = action.payload;
        }
      })
      .addCase(deleteAcademicTerm.fulfilled, (state, action) => {
        state.academicTerms = state.academicTerms.filter((t) => t.id !== action.payload);
      })
      // Grade Scales
      .addCase(fetchGradeScales.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchGradeScales.fulfilled, (state, action) => {
        state.loading = false;
        state.gradeScales = action.payload;
      })
      .addCase(fetchGradeScales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createGradeScale.fulfilled, (state, action) => {
        state.gradeScales.push(action.payload);
      })
      .addCase(updateGradeScale.fulfilled, (state, action) => {
        const index = state.gradeScales.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.gradeScales[index] = action.payload;
        }
      })
      .addCase(deleteGradeScale.fulfilled, (state, action) => {
        state.gradeScales = state.gradeScales.filter((s) => s.id !== action.payload);
      })
      // Exams
      .addCase(fetchExams.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchExams.fulfilled, (state, action) => {
        state.loading = false;
        state.exams = action.payload;
      })
      .addCase(fetchExams.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchExam.fulfilled, (state, action) => {
        state.currentExam = action.payload;
      })
      .addCase(createExam.fulfilled, (state, action) => {
        state.exams.push(action.payload);
      })
      .addCase(updateExam.fulfilled, (state, action) => {
        const index = state.exams.findIndex((e) => e.id === action.payload.id);
        if (index !== -1) {
          state.exams[index] = action.payload;
        }
      })
      .addCase(deleteExam.fulfilled, (state, action) => {
        state.exams = state.exams.filter((e) => e.id !== action.payload);
      })
      .addCase(fetchExamResults.fulfilled, (state, action) => {
        state.examResults = action.payload;
      })
      .addCase(fetchStudentExams.fulfilled, (state, action) => {
        state.exams = action.payload;
      })
      // Exam Results
      .addCase(fetchExamResultsList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchExamResultsList.fulfilled, (state, action) => {
        state.loading = false;
        state.examResults = action.payload;
      })
      .addCase(fetchExamResultsList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createExamResult.fulfilled, (state, action) => {
        state.examResults.push(action.payload);
      })
      .addCase(updateExamResult.fulfilled, (state, action) => {
        const index = state.examResults.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) {
          state.examResults[index] = action.payload;
        }
      })
      .addCase(deleteExamResult.fulfilled, (state, action) => {
        state.examResults = state.examResults.filter((r) => r.id !== action.payload);
      })
      // Assignments
      .addCase(fetchAssignments.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAssignments.fulfilled, (state, action) => {
        state.loading = false;
        state.assignments = action.payload;
      })
      .addCase(fetchAssignments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAssignment.fulfilled, (state, action) => {
        state.currentAssignment = action.payload;
      })
      .addCase(createAssignment.fulfilled, (state, action) => {
        state.assignments.push(action.payload);
      })
      .addCase(updateAssignment.fulfilled, (state, action) => {
        const index = state.assignments.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.assignments[index] = action.payload;
        }
      })
      .addCase(deleteAssignment.fulfilled, (state, action) => {
        state.assignments = state.assignments.filter((a) => a.id !== action.payload);
      })
      .addCase(fetchAssignmentSubmissions.fulfilled, (state, action) => {
        state.assignmentSubmissions = action.payload;
      })
      .addCase(fetchStudentAssignments.fulfilled, (state, action) => {
        state.assignments = action.payload;
      })
      // Assignment Submissions
      .addCase(fetchAssignmentSubmissionsList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAssignmentSubmissionsList.fulfilled, (state, action) => {
        state.loading = false;
        state.assignmentSubmissions = action.payload;
      })
      .addCase(fetchAssignmentSubmissionsList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createAssignmentSubmission.fulfilled, (state, action) => {
        state.assignmentSubmissions.push(action.payload);
      })
      .addCase(updateAssignmentSubmission.fulfilled, (state, action) => {
        const index = state.assignmentSubmissions.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.assignmentSubmissions[index] = action.payload;
        }
      })
      .addCase(deleteAssignmentSubmission.fulfilled, (state, action) => {
        state.assignmentSubmissions = state.assignmentSubmissions.filter((s) => s.id !== action.payload);
      })
      // Grade Entries
      .addCase(fetchGradeEntries.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchGradeEntries.fulfilled, (state, action) => {
        state.loading = false;
        state.gradeEntries = action.payload;
      })
      .addCase(fetchGradeEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createGradeEntry.fulfilled, (state, action) => {
        state.gradeEntries.push(action.payload);
      })
      .addCase(updateGradeEntry.fulfilled, (state, action) => {
        const index = state.gradeEntries.findIndex((g) => g.id === action.payload.id);
        if (index !== -1) {
          state.gradeEntries[index] = action.payload;
        }
      })
      .addCase(deleteGradeEntry.fulfilled, (state, action) => {
        state.gradeEntries = state.gradeEntries.filter((g) => g.id !== action.payload);
      })
      .addCase(fetchStudentGradeSummary.fulfilled, (state, action) => {
        state.gradeSummary = action.payload;
      })
      // Academic Plans
      .addCase(fetchAcademicPlans.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAcademicPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.academicPlans = action.payload;
      })
      .addCase(fetchAcademicPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAcademicPlan.fulfilled, (state, action) => {
        state.currentPlan = action.payload;
      })
      .addCase(createAcademicPlan.pending, (state) => {
        state.saving = true;
      })
      .addCase(createAcademicPlan.fulfilled, (state, action) => {
        state.saving = false;
        state.academicPlans.push(action.payload);
      })
      .addCase(createAcademicPlan.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateAcademicPlan.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateAcademicPlan.fulfilled, (state, action) => {
        state.saving = false;
        const index = state.academicPlans.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.academicPlans[index] = action.payload;
        }
      })
      .addCase(updateAcademicPlan.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(deleteAcademicPlan.fulfilled, (state, action) => {
        state.academicPlans = state.academicPlans.filter((p) => p.id !== action.payload);
      })
      // Reports
      .addCase(fetchStudentReportCard.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStudentReportCard.fulfilled, (state, action) => {
        state.loading = false;
        state.reportCard = action.payload;
      })
      .addCase(fetchStudentReportCard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchStudentTranscript.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStudentTranscript.fulfilled, (state, action) => {
        state.loading = false;
        state.transcript = action.payload;
      })
      .addCase(fetchStudentTranscript.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Student Activities
      .addCase(fetchStudentActivities.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStudentActivities.fulfilled, (state, action) => {
        state.loading = false;
        state.studentActivities = action.payload;
      })
      .addCase(fetchStudentActivities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createStudentActivity.pending, (state) => {
        state.saving = true;
      })
      .addCase(createStudentActivity.fulfilled, (state, action) => {
        state.saving = false;
        state.studentActivities.push(action.payload);
      })
      .addCase(createStudentActivity.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateStudentActivity.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateStudentActivity.fulfilled, (state, action) => {
        state.saving = false;
        const index = state.studentActivities.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.studentActivities[index] = action.payload;
        }
      })
      .addCase(updateStudentActivity.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(deleteStudentActivity.fulfilled, (state, action) => {
        state.studentActivities = state.studentActivities.filter((a) => a.id !== action.payload);
      })
      // Student Achievements
      .addCase(fetchStudentAchievements.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStudentAchievements.fulfilled, (state, action) => {
        state.loading = false;
        state.studentAchievements = action.payload;
      })
      .addCase(fetchStudentAchievements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createStudentAchievement.pending, (state) => {
        state.saving = true;
      })
      .addCase(createStudentAchievement.fulfilled, (state, action) => {
        state.saving = false;
        state.studentAchievements.push(action.payload);
      })
      .addCase(createStudentAchievement.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateStudentAchievement.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateStudentAchievement.fulfilled, (state, action) => {
        state.saving = false;
        const index = state.studentAchievements.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.studentAchievements[index] = action.payload;
        }
      })
      .addCase(updateStudentAchievement.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(deleteStudentAchievement.fulfilled, (state, action) => {
        state.studentAchievements = state.studentAchievements.filter((a) => a.id !== action.payload);
      });
  },
});

export const { clearError, clearReportCard, clearTranscript } = academicsSlice.actions;
export default academicsSlice.reducer;
